import "./config"; // Validate env config before anything else
import React from "react";
import { Provider } from "react-redux";
import { jwtDecode } from "jwt-decode";
import {
	loginSuccess,
	loginFailure,
	setPermissions,
} from "./actions/authActions";
import { render } from "react-dom";
import App from "./components/App";
import store from "./store";
import "./assets/styles/index.scss";
import {
	getCustomer,
	getItemFromLocalStorage,
	getItemFromSessionStorage,
	getPermissions,
	getReseller,
	getToken,
} from "./utils/local-storage.util";
import {
	getOwnCompany,
	setActiveExtractionJobId,
	setCustomer,
	updateBimifyProgress,
} from "./actions";
import { ACTIVE_EXTRACTION_JOB_ID_KEY } from "./reducers/globalWidgetReducer";
import * as Sentry from "@sentry/react";

const isProductionHost = window.location.hostname === "app.bimify.com";

const sentryIntegrations = [Sentry.browserTracingIntegration()];
if (isProductionHost) {
	sentryIntegrations.push(Sentry.replayIntegration());
}

Sentry.init({
	dsn: "https://daafa8dd6142729f4aa959f55a4d3542@o4509478460915712.ingest.de.sentry.io/4509478461964368",
	// Setting this option to true will send default PII data to Sentry.
	// For example, automatic IP address collection on events
	environment: window.location.hostname,
	sendDefaultPii: true,
	integrations: sentryIntegrations,
	beforeSend: (event) => {
		if (isProductionHost) {
			return event;
		}
		return null;
	},
	tracesSampleRate: isProductionHost ? 1.0 : 0,
	replaysSessionSampleRate: isProductionHost ? 1.0 : 0,
	replaysOnErrorSampleRate: isProductionHost ? 1.0 : 0,
	// tracePropagationTargets: ["localhost", /^https:\/\/app\.bimify\.com\/api/],
});

//Check if user is logged in on every page reload
const checkLogin = () => {
	if (getToken()) {
		const bimifyProgress = getItemFromLocalStorage("bimifyProgress")
			? JSON.parse(getItemFromLocalStorage("bimifyProgress"))
			: null;

		store.dispatch(loginSuccess(jwtDecode(getToken())));
		store.dispatch(setCustomer(getCustomer()));
		store.dispatch(setCustomer(getReseller(), true));
		// Load the logged-in user's own company (with canConsumeCustom* flags)
		// into global.ownCompany for permission gating — the JWT omits the flags.
		store.dispatch(getOwnCompany());
		if (!!getPermissions()) store.dispatch(setPermissions(getPermissions()));

		if (!!bimifyProgress) store.dispatch(updateBimifyProgress(bimifyProgress));

		// Rehydrate the per-tab extraction-initiator gate. sessionStorage survives
		// refresh, so the same tab that issued the BIMIFY/Verify request still
		// recognises itself as the initiator after a hard reload.
		// Gated on getToken() so a stale sessionStorage from a logged-out
		// session can never re-mount AutomateLabelingTool without auth.
		const storedJobId = getItemFromSessionStorage(ACTIVE_EXTRACTION_JOB_ID_KEY);
		if (storedJobId) {
			store.dispatch(setActiveExtractionJobId(storedJobId));
		}
	} else {
		store.dispatch(loginFailure("You are not logged in!"));
	}
};

checkLogin();
const root = document.getElementById("root");

render(
	<Provider store={store}>
		<App />
	</Provider>,
	root
);
